import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  InternalApiCreateNoteDraft,
  InternalApiDraftReferenceType,
  InternalApiNoteDraft,
  InternalApiUpdateNoteDraft,
} from '../api-internal';
import { noteDraftInternalApi } from '../apis';
import { useMe } from '../organisation/useMe';
import { CAN_USE_NOTE_DRAFT } from '../auth/permissions';

export const NOTE_DRAFTS_QUERY_KEY = 'noteDrafts';
export const getNoteDraftsQueryKey = (
  referenceId: number,
  referenceType: InternalApiDraftReferenceType,
  organisationId: number | undefined
) => [NOTE_DRAFTS_QUERY_KEY, referenceId, referenceType, organisationId] as const;

export const useQueryNoteDrafts = (referenceId: number, referenceType: InternalApiDraftReferenceType) => {
  const { permissions, currentOrganisation } = useMe();
  const isDraftEnabled = permissions.includes(CAN_USE_NOTE_DRAFT);

  return useQuery({
    queryKey: getNoteDraftsQueryKey(referenceId, referenceType, currentOrganisation?.id),
    queryFn: () => noteDraftInternalApi.findNoteDrafts({ referenceId, referenceType }),
    enabled: isDraftEnabled && !!referenceId && !!currentOrganisation?.id,
  });
};

export const useCreateNoteDraft = () => {
  return useMutation({
    mutationFn: (request: InternalApiCreateNoteDraft) =>
      noteDraftInternalApi.createNoteDraft({ internalApiCreateNoteDraft: request }),
  });
};

interface UpdateDraftParams {
  id: number;
  request: InternalApiUpdateNoteDraft;
}

export const useUpdateNoteDraft = () => {
  return useMutation({
    mutationFn: ({ id, request }: UpdateDraftParams) =>
      noteDraftInternalApi.updateNoteDraft({ id, internalApiUpdateNoteDraft: request }),
  });
};

interface UseDeleteDraftParams {
  referenceId: number;
  referenceType: InternalApiDraftReferenceType;
  organisationId: number | undefined;
}

interface DeleteDraftMutationParams {
  id: number;
}

export const useDeleteNoteDraft = ({ referenceId, referenceType, organisationId }: UseDeleteDraftParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: DeleteDraftMutationParams) => noteDraftInternalApi.deleteNoteDraft({ id }),
    onMutate: async (variables) => {
      if (!organisationId) return;
      const queryKey = getNoteDraftsQueryKey(referenceId, referenceType, organisationId);

      await queryClient.cancelQueries({ queryKey });

      const previousDrafts = queryClient.getQueryData<InternalApiNoteDraft[]>(queryKey);

      queryClient.setQueryData<InternalApiNoteDraft[]>(
        queryKey,
        (old) => old?.filter((draft) => draft.id !== variables.id) ?? []
      );

      return { previousDrafts, queryKey };
    },
    onError: (_, _variables, context) => {
      if (context?.previousDrafts && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousDrafts);
      }
    },
  });
};
